import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Save, Pencil, X, Upload, Download, FileSpreadsheet, AlertTriangle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { BackButton } from "@/components/BackButton";

const TEMPLATE_CSV = `Grade,Item,Qty,Required/Optional,Section,Price,Cost
8,A4 Hardcover Book 192 Page,2,Required,Stationery,32.99,21.50
8,HB Pencil,3,Required,Stationery,5.50,3.10
8,Mathematics Grade 8 Textbook,1,Required,Textbooks,289.99,215.00
R,Plastic Apron,1,Optional,Stationery,45.00,28.75`;

export default function AdminSchools() {
  const [schools, setSchools] = useState([]);
  const [newSchool, setNewSchool] = useState({ name: "", location: "", grades: "" });
  const [selected, setSelected] = useState(null);
  const [grade, setGrade] = useState("");
  const [products, setProducts] = useState([]);
  const [listItems, setListItems] = useState({});
  const [year, setYear] = useState("2026");
  const { catName } = useCategories();

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", location: "", grades: "" });
  const [uploadFor, setUploadFor] = useState(null);
  const [uploadGrade, setUploadGrade] = useState("");
  const [uploadYear, setUploadYear] = useState("2026");
  const [uploadSection, setUploadSection] = useState("");
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ name: "", price: "", stock: "", cost: "" });
  const [savingItem, setSavingItem] = useState(false);
  const [copyTo, setCopyTo] = useState({ school: "", grade: "" });
  const [copying, setCopying] = useState(false);
  const [manualItem, setManualItem] = useState({ name: "", price: "", stock: "", section: "textbooks" });
  const [addingManual, setAddingManual] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/schools").then((r) => setSchools(r.data.schools)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data.products.filter((p) => p.status === "active"))).catch(() => {});
  }, []);

  const loadList = (school, g) => {
    setSelected(school);
    setGrade(g);
    setEditingItem(null);
    api
      .get(`/schools/${school.id}/list`, { params: { grade: g } })
      .then((r) => {
        const map = {};
        r.data.items.forEach((it) => {
          if (it.product_id) map[it.product_id] = {
            qty: it.qty, required: it.required, section: it.section,
            name: it.name, price: it.effective_price, unavailable: !!it.unavailable, note: it.note || null,
            image: it.image || null, sku: it.sku || "", stock: it.stock ?? null,
            status: it.status || null, base_price: it.price ?? null,
          };
        });
        setListItems(map);
      })
      .catch(() => setListItems({}));
  };

  const startItemEdit = async (pid, item) => {
    setEditingItem(pid);
    setItemForm({ name: item.name || "", price: item.base_price != null ? String(item.base_price) : "",
                  stock: item.stock != null ? String(item.stock) : "", cost: "" });
    try {
      const r = await api.get(`/admin/products/${pid}`);
      const p = r.data.product;
      setItemForm({ name: p.name || "", price: p.price != null ? String(p.price) : "",
                    stock: p.stock != null ? String(p.stock) : "",
                    cost: p.cost_price != null ? String(p.cost_price) : "" });
    } catch { /* keep the row's own data */ }
  };

  const saveItemEdit = async (pid) => {
    const payload = {};
    if (itemForm.name.trim() && itemForm.name.trim() !== listItems[pid]?.name) payload.name = itemForm.name.trim();
    if (itemForm.price !== "") payload.price = parseFloat(itemForm.price);
    if (itemForm.stock !== "") payload.stock = parseInt(itemForm.stock, 10);
    if (itemForm.cost !== "") payload.cost_price = parseFloat(itemForm.cost);
    if (!Object.keys(payload).length) { setEditingItem(null); return; }
    setSavingItem(true);
    try {
      const r = await api.patch(`/admin/products/${pid}/quick`, payload);
      const p = r.data.product;
      const unavail = p.status !== "active" || !p.price;
      setListItems((m) => ({ ...m, [pid]: { ...m[pid], name: p.name, price: p.price, base_price: p.price, stock: p.stock, status: p.status,
                                            image: p.image || m[pid].image, unavailable: unavail || p.stock <= 0,
                                            note: unavail ? "Price coming soon" : (p.stock > 0 ? null : "Out of stock") } }));
      setProducts((ps) => (ps.some((x) => x.id === pid) ? ps.map((x) => (x.id === pid ? p : x)) : [...ps, p]));
      toast.success(`${p.name} updated — ${formatR(p.price)}, stock ${p.stock}. Live everywhere instantly.`);
      setEditingItem(null);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSavingItem(false);
    }
  };

  const addManualItem = async (e) => {
    e.preventDefault();
    const name = manualItem.name.trim();
    if (!name) { toast.error("Type the item name first"); return; }
    const price = manualItem.price === "" ? 0 : parseFloat(manualItem.price);
    if (Number.isNaN(price) || price < 0) { toast.error("Price must be 0 or more"); return; }
    const stock = manualItem.stock === "" ? 0 : parseInt(manualItem.stock, 10);
    setAddingManual(true);
    try {
      // Creates the catalogue product (textbook or stationery) AND puts it on this
      // grade's list in one step — exactly for the "school sent one more book" case.
      const r = await api.post("/admin/products", {
        name, sku: "", barcode: "", isbn: "",
        category: manualItem.section === "textbooks" ? "textbooks" : "stationery",
        subcategory: "", description: "", image: "",
        price, cost_price: null, promo_price: null, promo_start: null, promo_end: null,
        stock: Number.isNaN(stock) ? 0 : stock, supplier: "", featured: false,
        status: price > 0 ? "active" : "draft",
      });
      const p = r.data.product;
      const newMap = { ...listItems, [p.id]: { qty: 1, required: true, section: manualItem.section,
        name: p.name, price: p.price || null, base_price: p.price || null, stock: p.stock,
        status: p.status, image: null, sku: p.sku || "", unavailable: price <= 0,
        note: price <= 0 ? "Price coming soon" : null } };
      setListItems(newMap);
      setProducts((ps) => (ps.some((x) => x.id === p.id) ? ps : [...ps, p]));
      setManualItem({ name: "", price: "", stock: "", section: "textbooks" });
      // Save immediately — a manually added item must persist without a second click
      const saved = await saveList(newMap);
      if (saved) {
        toast.success(`"${name}" added to ${selected.name} Gr ${grade} and published — it's also in Products → ${manualItem.section === "textbooks" ? "Textbooks" : "Stationery"}${price > 0 ? ` at ${formatR(price)}` : " (set its price with Edit)"}`);
      } else {
        toast.error(`"${name}" was created under Products but the list didn't save — press Save list`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setAddingManual(false);
    }
  };

  const copyList = async () => {
    const target = schools.find((s) => s.id === copyTo.school);
    const tg = copyTo.grade.trim();
    if (!target || !tg) { toast.error("Choose a school and enter a grade to copy to"); return; }
    const items = Object.entries(listItems).map(([product_id, v]) => ({
      product_id, qty: Number(v.qty) || 1, required: !!v.required, section: v.section || "stationery",
    }));
    if (!items.length) { toast.error("This list is empty — nothing to copy"); return; }
    if (!window.confirm(`Copy ${items.length} item(s) from ${selected.name} Gr ${grade} to ${target.name} — Grade ${tg}? This replaces that grade's existing list.`)) return;
    setCopying(true);
    try {
      await api.put(`/admin/schools/${target.id}/lists/${tg}`, { year, items });
      toast.success(`Copied ${items.length} items to ${target.name} — Grade ${tg}. Live for parents instantly.`);
      setCopyTo({ school: "", grade: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCopying(false);
    }
  };

  const createSchool = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/schools", {
        name: newSchool.name,
        location: newSchool.location,
        grades: newSchool.grades.split(",").map((g) => g.trim()).filter(Boolean),
      });
      toast.success("School added — live on Assorted Schools");
      setNewSchool({ name: "", location: "", grades: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setEditForm({ name: s.name, location: s.location || "", grades: (s.grades || []).join(", ") });
    setUploadFor(null);
    setReport(null);
  };

  const saveEdit = async (s) => {
    const grades = editForm.grades.split(",").map((g) => g.trim()).filter(Boolean);
    const removed = (s.grades || []).filter((g) => !grades.includes(g));
    if (removed.length && !window.confirm(`Removing grade(s) ${removed.map((g) => "Gr " + g).join(", ")} will also delete their saved lists. Continue?`)) return;
    try {
      await api.put(`/admin/schools/${s.id}`, { name: editForm.name, location: editForm.location, grades });
      toast.success("School updated — live on Assorted Schools");
      if (selected?.id === s.id && removed.includes(grade)) { setGrade(""); setListItems({}); }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const deleteSchool = async (s) => {
    if (!window.confirm(`Delete "${s.name}" and all its grade lists?`)) return;
    try {
      await api.delete(`/admin/schools/${s.id}`);
      toast.success(`"${s.name}" removed`);
      if (selected?.id === s.id) { setSelected(null); setGrade(""); }
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveList = async (itemsOverride) => {
    const source = itemsOverride || listItems;
    const items = Object.entries(source).map(([product_id, v]) => ({
      product_id, qty: Number(v.qty) || 1, required: !!v.required, section: v.section || "stationery",
    }));
    try {
      await api.put(`/admin/schools/${selected.id}/lists/${grade}`, { year, items });
      toast.success(`Grade ${grade} list saved (${items.length} items) — live for parents`);
      return true;
    } catch (err) {
      toast.error(errMsg(err));
      return false;
    }
  };

  const toggleProduct = (p) => {
    const map = { ...listItems };
    if (map[p.id]) delete map[p.id];
    else map[p.id] = { qty: 1, required: true, section: ["textbooks", "religious-books"].includes(p.category) ? "textbooks" : "stationery",
                       name: p.name, price: p.effective_price, unavailable: false, note: null,
                       image: p.image || null, sku: p.sku || "", stock: p.stock ?? null,
                       status: p.status || "active", base_price: p.price ?? null };
    setListItems(map);
  };

  const removeItem = (pid) => {
    const map = { ...listItems };
    delete map[pid];
    setListItems(map);
  };

  const submitUpload = async (s) => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose a CSV or Excel file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("grade", uploadGrade);
    fd.append("year", uploadYear || "2026");
    fd.append("section", uploadSection);
    setUploading(true);
    setReport(null);
    try {
      const r = await api.post(`/admin/schools/${s.id}/lists/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(r.data);
      toast.success(`List imported — ${Object.values(r.data.grades).reduce((a, b) => a + b, 0)} items across ${Object.keys(r.data.grades).length} grade(s)`);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "btmedu-school-list-template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div data-testid="admin-schools">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy mb-1">Schools & prescribed lists</h1>
          <p className="text-sm text-slate-500">Edit schools, upload a school's list file to auto-populate grades, or fine-tune each grade below. Parents see changes instantly.</p>
        </div>
        <button onClick={downloadTemplate} className="h-9 px-4 rounded-full border border-navy/20 text-navy text-xs font-bold flex items-center gap-1.5 hover:bg-navy hover:text-white transition-colors" data-testid="download-template-btn">
          <Download size={13} /> List template (CSV)
        </button>
      </div>

      <form onSubmit={createSchool} className="bg-white rounded-2xl border border-slate-200/80 p-5 flex gap-3 flex-wrap items-end mb-6">
        <label className="flex-1 min-w-[180px]">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">School name</span>
          <input required value={newSchool.name} onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="new-school-name" />
        </label>
        <label className="min-w-[160px]">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Location</span>
          <input value={newSchool.location} onChange={(e) => setNewSchool({ ...newSchool, location: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="new-school-location" />
        </label>
        <label className="min-w-[200px]">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Grades (comma separated)</span>
          <input required placeholder="R, 1, 2, 8, 12" value={newSchool.grades} onChange={(e) => setNewSchool({ ...newSchool, grades: e.target.value })} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none" data-testid="new-school-grades" />
        </label>
        <button className="h-10 px-5 rounded-full bg-navy text-white text-sm font-bold flex items-center gap-1.5" data-testid="add-school-btn">
          <Plus size={14} /> Add school
        </button>
      </form>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6 items-start">
        <div className="space-y-2.5" data-testid="schools-list">
          {schools.map((s) => (
            <div key={s.id} className={`bg-white rounded-2xl border p-4 ${selected?.id === s.id ? "border-gold" : "border-slate-200/80"}`} data-testid={`admin-school-${s.id}`}>
              {editing === s.id ? (
                <div className="space-y-2.5" data-testid={`edit-school-form-${s.id}`}>
                  <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-gold" placeholder="School name" data-testid="edit-school-name" />
                  <input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm outline-none" placeholder="Location" data-testid="edit-school-location" />
                  <input value={editForm.grades} onChange={(e) => setEditForm({ ...editForm, grades: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm outline-none" placeholder="Grades e.g. R, 1, 2, 8" data-testid="edit-school-grades" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(s)} className="flex-1 h-9 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center gap-1.5" data-testid="save-school-edit-btn">
                      <Save size={13} /> Save
                    </button>
                    <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-full border border-slate-200 text-slate-500 text-xs font-bold flex items-center gap-1" data-testid="cancel-school-edit-btn">
                      <X size={13} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-display font-bold text-navy text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.location}</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => startEdit(s)} className="p-1.5 text-slate-400 hover:text-navy" data-testid={`edit-school-${s.id}`} aria-label="Edit school">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => { setUploadFor(uploadFor === s.id ? null : s.id); setReport(null); setEditing(null); }} className="p-1.5 text-slate-400 hover:text-gold" data-testid={`upload-list-${s.id}`} aria-label="Upload list file">
                        <Upload size={14} />
                      </button>
                      <button onClick={() => deleteSchool(s)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-school-${s.id}`} aria-label="Delete school">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(s.grades || []).map((g) => (
                      <button
                        key={g}
                        onClick={() => loadList(s, g)}
                        className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition-colors ${selected?.id === s.id && grade === g ? "bg-navy text-white" : "bg-cream text-navy hover:bg-gold/20"}`}
                        data-testid={`admin-grade-${s.id}-${g}`}
                      >
                        Gr {g}
                      </button>
                    ))}
                  </div>
                  {uploadFor === s.id && (
                    <div className="mt-3 pt-3 border-t border-slate-100 space-y-2.5" data-testid={`upload-panel-${s.id}`}>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                        <FileSpreadsheet size={12} /> Upload list file (CSV / Excel)
                      </p>
                      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="w-full text-xs text-slate-500 file:mr-2 file:rounded-full file:border-0 file:bg-cream file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-navy hover:file:bg-gold/20" data-testid="upload-file-input" />
                      <div className="flex gap-2">
                        <input value={uploadGrade} onChange={(e) => setUploadGrade(e.target.value)} placeholder="Grade (if not in file)" className="flex-1 h-8 rounded-lg border border-slate-200 px-2.5 text-xs outline-none" data-testid="upload-grade-override" />
                        <input value={uploadYear} onChange={(e) => setUploadYear(e.target.value)} className="w-20 h-8 rounded-lg border border-slate-200 px-2.5 text-xs text-center outline-none" data-testid="upload-year" />
                      </div>
                      <select value={uploadSection} onChange={(e) => setUploadSection(e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold text-navy bg-white outline-none" data-testid="upload-section">
                        <option value="">Auto-detect item types from the file</option>
                        <option value="stationery">This is a STATIONERY list → items go to Stationery</option>
                        <option value="textbooks">This is a TEXTBOOK list → items go to Textbooks</option>
                      </select>
                      <button onClick={() => submitUpload(s)} disabled={uploading} className="w-full h-9 rounded-full bg-gold hover:bg-gold-hover text-white text-xs font-bold flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors" data-testid="upload-submit-btn">
                        <Upload size={13} /> {uploading ? "Importing…" : "Import & populate grades"}
                      </button>
                      {report && (
                        <div className="rounded-xl bg-cream/70 border border-gold/30 p-3 space-y-2 text-xs" data-testid="upload-report">
                          <p className="font-bold text-navy">
                            Populated: {Object.entries(report.grades).map(([g, n]) => `Gr ${g} (${n})`).join(", ")}
                          </p>
                          <p className="text-slate-600">
                            {report.matched} matched to catalogue · {report.created_count} new products
                            {report.created_count > 0 && (
                              <span className="block mt-0.5 font-semibold text-navy">
                                → {report.created_stationery} into Stationery · {report.created_textbooks} into Textbooks
                              </span>
                            )}
                            {(report.created_priced > 0 || report.priced_from_file > 0) && (
                              <span className="block mt-0.5 font-semibold text-emerald-600">
                                ✓ {report.created_priced + report.priced_from_file} item(s) priced from the file and live immediately
                              </span>
                            )}
                            {report.costs_updated > 0 && (
                              <span className="block mt-0.5 font-semibold text-navy" data-testid="upload-costs-note">
                                ✓ {report.costs_updated} item cost(s) captured from the file — visible in the product editor and exports
                              </span>
                            )}
                          </p>
                          {report.matched_items?.length > 0 && (
                            <details className="text-slate-600" data-testid="upload-matched-list">
                              <summary className="cursor-pointer font-bold text-navy text-[11px]">Review {report.matched_items.length} catalogue match(es)</summary>
                              <ul className="mt-1 max-h-28 overflow-y-auto space-y-0.5 text-[11px]">
                                {report.matched_items.map((m, i) => <li key={i}>{m.item} <span className="text-slate-400">→</span> {m.matched_to} (Gr {m.grade})</li>)}
                              </ul>
                            </details>
                          )}
                          {report.created_count > 0 && (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-amber-800" data-testid="upload-created-notice">
                              <p className="font-bold flex items-center gap-1"><AlertTriangle size={12} /> {report.created_count} item(s) created as drafts at R0 / out of stock</p>
                              <p className="mt-0.5">Set their prices under <span className="font-bold">Products → filter "Draft (needs price)"</span> before parents can buy them:</p>
                              <ul className="mt-1 max-h-28 overflow-y-auto space-y-0.5 font-mono text-[11px]">
                                {report.created.map((c) => <li key={c.id}>{c.sku} — {c.name} (Gr {c.grade})</li>)}
                              </ul>
                            </div>
                          )}
                          {report.ambiguous > 0 && (
                            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5 text-blue-800" data-testid="upload-ambiguous-note">
                              <p className="font-bold flex items-center gap-1"><AlertTriangle size={12} /> {report.ambiguous} row(s) matched several similar catalogue products</p>
                              <p className="mt-0.5">They were added as new items carrying the spreadsheet's exact wording — the uploader never guesses between look-alike products. Check them under Products and merge if one is a duplicate.</p>
                            </div>
                          )}
                          {report.skipped?.length > 0 && (
                            <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 text-red-700" data-testid="upload-skipped-notice">
                              <p className="font-bold">{report.skipped.length} row(s) skipped</p>
                              <ul className="mt-1 max-h-24 overflow-y-auto space-y-0.5 text-[11px]">
                                {report.skipped.map((sk, i) => <li key={i}>Row {sk.row}: {sk.text} — {sk.reason}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {selected && grade ? (
          <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="list-editor">
            <div className="mb-4">
              <BackButton onClick={() => { setSelected(null); setGrade(""); setListItems({}); }} label="Back to schools" testId="editor-back-button" />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
              <h3 className="font-display font-bold text-navy">{selected.name} — Grade {grade} list</h3>
              <div className="flex items-center gap-2">
                <input value={year} onChange={(e) => setYear(e.target.value)} className="h-9 w-24 rounded-lg border border-slate-200 px-3 text-sm text-center" data-testid="list-year" />
                <button onClick={saveList} className="h-9 px-5 rounded-full bg-gold hover:bg-gold-hover text-white text-sm font-bold flex items-center gap-1.5 transition-colors" data-testid="save-list-btn">
                  <Save size={14} /> Save list
                </button>
              </div>
            </div>
            {Object.keys(listItems).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-5 rounded-xl bg-cream/60 border border-gold/30 p-3" data-testid="copy-list-panel">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Copy size={12} /> Copy this list to:</p>
                <select value={copyTo.school} onChange={(e) => setCopyTo({ ...copyTo, school: e.target.value })} className="h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white max-w-[220px]" data-testid="copy-school-select">
                  <option value="">Choose school…</option>
                  {schools.filter((s) => s.id !== selected.id).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input value={copyTo.grade} onChange={(e) => setCopyTo({ ...copyTo, grade: e.target.value })} placeholder="Grade e.g. 3" className="w-28 h-8 rounded-lg border border-slate-200 px-2 text-xs" data-testid="copy-grade-input" />
                <button onClick={copyList} disabled={copying} className="h-8 px-4 rounded-full bg-navy text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="copy-list-btn">
                  <Copy size={12} /> {copying ? "Copying…" : `Copy ${Object.keys(listItems).length} items`}
                </button>
              </div>
            )}
            <p className="text-xs text-slate-500 mb-4">Tick products to include them, set prescribed quantity and whether required or optional.</p>

            {Object.keys(listItems).length > 0 && (
              <div className="mb-5 rounded-2xl border border-gold/40 bg-cream/40 p-4" data-testid="list-current-items">
                <p className="text-[11px] font-bold uppercase tracking-wider text-navy mb-1">
                  On this list — {Object.keys(listItems).length} item{Object.keys(listItems).length !== 1 ? "s" : ""}
                </p>
                <p className="text-[11px] text-slate-500 mb-2.5">Press <span className="font-bold text-gold">Edit</span> on any item to change its name, price or stock — it updates on the website instantly.</p>
                <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                  {Object.entries(listItems).map(([pid, item]) => (
                    <div key={pid} className={`rounded-xl border p-3 text-sm bg-white ${item.unavailable ? "border-amber-300" : "border-gold/50"}`} data-testid={`list-current-${pid}`}>
                      <div className="flex items-center gap-3">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-11 h-11 rounded-lg object-cover border border-slate-100 shrink-0" data-testid={`list-current-img-${pid}`} />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-cream border border-slate-100 flex items-center justify-center text-[9px] font-bold text-slate-400 shrink-0" data-testid={`list-current-img-${pid}`}>No pic</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy truncate">{item.name || pid}</p>
                          <p className="text-[11px] text-slate-400 truncate">
                            {item.sku && <span className="font-mono mr-2">{item.sku}</span>}
                            {item.price ? <span className="font-mono">{formatR(item.price)}</span> : <span className="text-amber-600 font-semibold">No price</span>}
                            {item.stock != null && <span className="ml-2">stock {item.stock}</span>}
                            {item.unavailable && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full">{item.note || "Needs pricing"}</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <input type="number" min="1" value={item.qty} onChange={(e) => setListItems({ ...listItems, [pid]: { ...item, qty: e.target.value } })} className="w-14 h-8 rounded-lg border border-slate-200 text-center text-xs font-bold" data-testid={`list-current-qty-${pid}`} />
                          <select value={item.required ? "req" : "opt"} onChange={(e) => setListItems({ ...listItems, [pid]: { ...item, required: e.target.value === "req" } })} className="h-8 rounded-lg border border-slate-200 px-1.5 text-xs" data-testid={`list-current-req-${pid}`}>
                            <option value="req">Required</option>
                            <option value="opt">Optional</option>
                          </select>
                          <select value={item.section} onChange={(e) => setListItems({ ...listItems, [pid]: { ...item, section: e.target.value } })} className="h-8 rounded-lg border border-slate-200 px-1.5 text-xs" data-testid={`list-current-sec-${pid}`}>
                            <option value="stationery">Stationery</option>
                            <option value="textbooks">Textbooks</option>
                          </select>
                          <button onClick={() => (editingItem === pid ? setEditingItem(null) : startItemEdit(pid, item))} className={`h-8 px-3 rounded-full text-xs font-bold flex items-center gap-1 border transition-colors ${editingItem === pid ? "bg-gold text-white border-gold" : "border-gold text-gold hover:bg-gold hover:text-white"}`} data-testid={`list-current-edit-${pid}`} aria-label="Edit item">
                            <Pencil size={12} /> Edit
                          </button>
                          <Link to={`/admin-login/products?edit=${pid}`} className="h-8 px-3 rounded-full border border-navy/30 text-navy text-xs font-bold flex items-center gap-1 hover:bg-navy hover:text-white transition-colors" data-testid={`list-current-open-${pid}`} title="Open the full product editor — picture, category, barcode and more">
                            <ExternalLink size={12} /> Full edit
                          </Link>
                          <button onClick={() => removeItem(pid)} className="h-8 px-2.5 rounded-full border border-slate-200 text-xs font-bold text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center gap-1" data-testid={`list-current-remove-${pid}`} aria-label="Remove from list">
                            <Trash2 size={12} /> Remove
                          </button>
                        </div>
                      </div>
                      {editingItem === pid && (
                        <div className="mt-2.5 pt-2.5 border-t border-slate-100 space-y-2" data-testid={`list-current-edit-panel-${pid}`}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-[11px] font-bold text-slate-500">Item name</label>
                            <input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className="flex-1 min-w-[220px] h-8 rounded-lg border border-slate-200 px-2 text-xs font-semibold" data-testid={`list-current-edit-name-${pid}`} />
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="text-[11px] font-bold text-slate-500">Price (R)</label>
                            <input type="number" min="0.01" step="0.01" value={itemForm.price} onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })} placeholder="e.g. 24.99" className="w-24 h-8 rounded-lg border border-slate-200 px-2 text-xs font-bold" data-testid={`list-current-edit-price-${pid}`} />
                            <label className="text-[11px] font-bold text-slate-500">Cost (R, admin only)</label>
                            <input type="number" min="0" step="0.01" value={itemForm.cost} onChange={(e) => setItemForm({ ...itemForm, cost: e.target.value })} placeholder="e.g. 15.00" className="w-24 h-8 rounded-lg border border-slate-200 px-2 text-xs font-bold" data-testid={`list-current-edit-cost-${pid}`} />
                            <label className="text-[11px] font-bold text-slate-500">Stock</label>
                            <input type="number" min="0" value={itemForm.stock} onChange={(e) => setItemForm({ ...itemForm, stock: e.target.value })} placeholder="e.g. 50" className="w-20 h-8 rounded-lg border border-slate-200 px-2 text-xs font-bold" data-testid={`list-current-edit-stock-${pid}`} />
                            <button onClick={() => saveItemEdit(pid)} disabled={savingItem} className="h-8 px-4 rounded-full bg-gold hover:bg-gold-hover text-white text-xs font-bold flex items-center gap-1 disabled:opacity-50" data-testid={`list-current-edit-save-${pid}`}>
                              <Save size={12} /> {savingItem ? "Saving…" : "Save changes"}
                            </button>
                            <button onClick={() => setEditingItem(null)} className="h-8 px-3 rounded-full border border-slate-200 text-xs font-bold text-slate-500 flex items-center gap-1" data-testid={`list-current-edit-cancel-${pid}`}>
                              <X size={12} /> Cancel
                            </button>
                            {item.status === "draft" && <span className="text-[10px] text-amber-600 font-semibold">Draft — saving a price publishes it live</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-2">Changes apply when you press Save list above.</p>
              </div>
            )}

            <form onSubmit={addManualItem} className="mb-5 rounded-2xl border border-navy/25 bg-navy/[0.03] p-4" data-testid="manual-add-box">
              <p className="text-[11px] font-bold uppercase tracking-wider text-navy mb-2.5 flex items-center gap-1.5">
                <Plus size={12} /> Add an item manually to this grade
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <input value={manualItem.name} onChange={(e) => setManualItem({ ...manualItem, name: e.target.value })}
                  placeholder="Item name e.g. English Today Grade 8 Learner's Book"
                  className="flex-1 min-w-[240px] h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="manual-add-name" />
                <input type="number" min="0" step="0.01" value={manualItem.price} onChange={(e) => setManualItem({ ...manualItem, price: e.target.value })}
                  placeholder="Price R (optional)" className="w-32 h-9 rounded-lg border border-slate-200 px-2.5 text-xs bg-white" data-testid="manual-add-price" />
                <input type="number" min="0" value={manualItem.stock} onChange={(e) => setManualItem({ ...manualItem, stock: e.target.value })}
                  placeholder="Stock (optional)" className="w-32 h-9 rounded-lg border border-slate-200 px-2.5 text-xs bg-white" data-testid="manual-add-stock" />
                <select value={manualItem.section} onChange={(e) => setManualItem({ ...manualItem, section: e.target.value })}
                  className="h-9 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="manual-add-section">
                  <option value="textbooks">Textbooks</option>
                  <option value="stationery">Stationery</option>
                </select>
                <button disabled={addingManual} className="h-9 px-5 rounded-full bg-navy hover:bg-navy-light text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="manual-add-btn">
                  <Plus size={13} /> {addingManual ? "Adding…" : "Add to list"}
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">For when a school sends one or two extra items after the main upload. Saves and publishes instantly — the item is also created under Products (Textbooks or Stationery) so it can be priced, pictured and edited there too. No price yet? It shows as "Price coming soon" until you Edit it.</p>
            </form>

            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Or add an existing product to the list</p>
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {products.map((p) => {
                const item = listItems[p.id];
                return (
                  <div key={p.id} className={`rounded-xl border p-3 flex items-center gap-3 text-sm ${item ? "border-gold bg-gold/5" : "border-slate-100"}`} data-testid={`list-editor-product-${p.sku || p.id}`}>
                    <input type="checkbox" checked={!!item} onChange={() => toggleProduct(p)} className="w-4 h-4 accent-[#E8722A]" data-testid={`list-editor-check-${p.sku || p.id}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-400">{catName(p.category)} · <span className="font-mono">{formatR(p.effective_price)}</span> · stock {p.stock}</p>
                    </div>
                    {item && (
                      <div className="flex items-center gap-2">
                        <input type="number" min="1" value={item.qty} onChange={(e) => setListItems({ ...listItems, [p.id]: { ...item, qty: e.target.value } })} className="w-14 h-8 rounded-lg border border-slate-200 text-center text-xs font-bold" data-testid={`list-editor-qty-${p.sku || p.id}`} />
                        <select value={item.required ? "req" : "opt"} onChange={(e) => setListItems({ ...listItems, [p.id]: { ...item, required: e.target.value === "req" } })} className="h-8 rounded-lg border border-slate-200 px-1.5 text-xs" data-testid={`list-editor-req-${p.sku || p.id}`}>
                          <option value="req">Required</option>
                          <option value="opt">Optional</option>
                        </select>
                        <select value={item.section} onChange={(e) => setListItems({ ...listItems, [p.id]: { ...item, section: e.target.value } })} className="h-8 rounded-lg border border-slate-200 px-1.5 text-xs" data-testid={`list-editor-sec-${p.sku || p.id}`}>
                          <option value="stationery">Stationery</option>
                          <option value="textbooks">Textbooks</option>
                        </select>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-cream rounded-3xl border border-gold/30 p-12 text-center text-sm text-slate-500" data-testid="list-editor-empty">
            Select a school and grade to build its prescribed list, or upload a list file on the school card.
          </div>
        )}
      </div>
    </div>
  );
}
